import { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import useApiHook from "../../hooks/useApiHook";
import cancelButtonSx from "../../styles/cancelButtonSx";

const ForgotPasswordModal = ({ open, onClose }) => {
    const emailElement = useRef('');
    const { makeRequest, response, error } = useApiHook();
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasAttempted, setHasAttempted] = useState(false);

    useEffect(() => {
        if (!open) {
            setEmailIsValid(true);
            setLoading(false);
            setSubmitted(false);
            setHasAttempted(false);
        }
    }, [open]);

    useEffect(() => {
        if (!hasAttempted) {
            return;
        }

        if (!response && !error) {
            return;
        }

        setLoading(false);
        if (response?.state === 'success') {
            setSubmitted(true);
        }
    }, [response, error, hasAttempted]);

    const validateEmail = () => {
        const isValid = /^\S+@\S+\.\S+$/.test(emailElement.current.value.trim());
        setEmailIsValid(isValid);
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateEmail()) {
            return;
        }

        setLoading(true);
        setHasAttempted(true);
        await makeRequest('/auth/forgot-password', 'post', {
            email: emailElement.current.value.trim().toLowerCase()
        });
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>Hai dimenticato la password?</DialogTitle>
            <DialogContent>
                {!submitted ? (
                    <form id="forgot-password-form" onSubmit={handleSubmit}>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Inserisci la tua email e ti invieremo un link per reimpostare la password.
                            </Typography>
                            <TextField
                                id="forgot-password-email-input"
                                label="Email"
                                type="email"
                                autoComplete="email"
                                fullWidth
                                inputRef={emailElement}
                                error={!emailIsValid || error?.error?.code === 400}
                                helperText={!emailIsValid || error?.error?.code === 400 ? "Inserisci un indirizzo email valido" : ""}
                                onBlur={validateEmail}
                            />
                            {error && error?.error?.code !== 400 ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <ErrorIcon color="error" />
                                    <Typography color="error" variant="body2">
                                        Non siamo riusciti a inviare l'email di reset. Riprova tra poco.
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>
                    </form>
                ) : (
                    <Box sx={{ pt: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <CheckIcon color="success" sx={{ mt: 0.25 }} />
                            <Typography variant="body2">
                                Se l'indirizzo esiste nei nostri sistemi, abbiamo inviato un link per reimpostare la password.
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={loading} sx={cancelButtonSx}>
                    {submitted ? 'Chiudi' : 'Annulla'}
                </Button>
                {!submitted ? (
                    <Button type="submit" form="forgot-password-form" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} /> : 'Invia email'}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
};

export default ForgotPasswordModal;
